import { ApolloError } from '@apollo/client';
import { getColumnSummaries, isGroupSummaryTableCell, isTotalSummaryTableCell } from '@devexpress/dx-grid-core';
import { Template, TemplateConnector } from '@devexpress/dx-react-core';
import {
  DataTypeProvider,
  DataTypeProviderProps,
  GroupingState,
  IntegratedGrouping,
  IntegratedSummary,
  SummaryState,
  Table as TableBase,
  TableColumn,
  TableGroupRow as TableGroupRowBase,
  TableSummaryRow as TableSummaryRowBase,
} from '@devexpress/dx-react-grid';
import { Grid, Table, TableGroupRow, TableHeaderRow, TableSummaryRow } from '@devexpress/dx-react-grid-material-ui';
import { ChevronRight, ExpandMore } from '@mui/icons-material';
import { Card, CardContent, IconButton, Grid as MUIGrid, TableCell, Tooltip } from '@mui/material';
import { Theme } from '@mui/material/styles';
import clsx from 'clsx';
import * as Schema from 'generated/graphql/schema';
import * as i18next from 'i18next';
import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { compose } from 'recompose';

import { DurationTypeProvider, StopsCauseTypeProvider } from '@/components/data-type-providers';
import { WithUtils, withUtils } from '@/hocs';
import { WithStyles, withStyles } from '@/hocs/with-styles';

const styles = (theme: Theme) => ({
  card: {
    display: 'block',
    minHeight: '250px',
  },
  cell: {
    cursor: 'pointer',
    paddingLeft: theme.spacing(),
    paddingRight: theme.spacing(),
    paddingTop: theme.spacing(-0.5),
  },
  groupButton: {
    verticalAlign: 'middle',
    display: 'inline-block',
    padding: theme.spacing(),
    marginRight: theme.spacing(),
  },
  columnTitle: {
    verticalAlign: 'middle',
  },
  customRow: {
    backgroundColor: '#ececec',
  },
  customTotalRow: {
    backgroundColor: 'rgb(143, 188, 143)',
    '& > td': {
      fontWeight: 500,
    },
  },
  wordSpace: {
    wordSpacing: '0.25em',
  },
});

export interface Data {
  timeRange: Omit<Schema.TimeRange, '__typename'>;
  oee: Pick<Schema.Oee, 'scrapLoss' | 'speedLoss' | 'totalEquipmentTime'> | null;
  stopStats: Schema.StopCauseStat[];
}

const NameTooltipFormatter = ({ value, row }: DataTypeProvider.ValueFormatterProps) => {
  return (
    <Tooltip placement="top" title={row.description}>
      <span>{value}</span>
    </Tooltip>
  );
};

export const NameTooltipProvider = (
  props: DataTypeProviderProps & {
    for: string[];
    theme: Theme;
  },
) => <DataTypeProvider formatterComponent={NameTooltipFormatter} {...props} />;

const GroupCellWithoutGroupnameBase = ({
  colSpan,
  row,
  column,
  expanded,
  onToggle,
  classes,
  children,
  className,
  tableRow,
  tableColumn,
  ...restProps
}: TableGroupRowBase.CellProps &
  WithStyles<typeof styles> & {
    children: React.ReactChildren;
    className: string;
  }) => {
  const handleClick = () => onToggle();

  return (
    <TableCell colSpan={colSpan} className={clsx(classes.cell, className)} onClick={handleClick} {...restProps}>
      <IconButton className={classes.groupButton} size="large">
        {expanded ? <ExpandMore /> : <ChevronRight />}
      </IconButton>
      <span className={classes.columnTitle}>
        <strong>{children || row.value}</strong>
      </span>
    </TableCell>
  );
};

const GroupCellWithoutGroupname = withStyles(styles, {
  name: 'GroupCellWithoutGroupname',
})(GroupCellWithoutGroupnameBase) as React.ComponentType<TableGroupRowBase.CellProps>;

const GroupRowWithColSpanBase =
  (customClass: string) =>
  ({
    classes,
    className,
    children,
    ...restProps
  }: WithStyles<typeof styles> & {
    children: React.ReactNode[];
    className?: string;
  } & TableBase.DataRowProps) => {
    return <Table.Row className={customClass} children={children} {...restProps} />;
  };

const GroupRowWithColSpan = (customClass: string) =>
  withStyles(styles, {
    name: 'GroupRowWithColSpan',
  })(GroupRowWithColSpanBase(customClass)) as React.ComponentType<TableBase.RowProps>;

const OEESummaryCalculator =
  (
    t: i18next.TFunction,
    totalTime: number = 0,
    allRows: Row[],
    classes: WithStyles<typeof styles>['classes'],
    utils: WithUtils['utils'],
  ) =>
  (type: string, rows: Row[], getValue: (row: Row) => unknown) => {
    const cdot = ' ' + String.fromCharCode(183) + ' ';
    const rightArrow = ' ' + String.fromCharCode(8658) + ' ';
    if (type === 'identifierSumTotal') {
      return (
        <span className={classes.wordSpace}>
          TCU ={' '}
          {t(['oee:availability'], {
            defaultValue: 'Availability',
          })}
          {cdot}
          {t(['oee:performance'], {
            defaultValue: 'Performance',
          })}
          {cdot}
          {t(['oee:quality'], {
            defaultValue: 'Quality',
          })}
        </span>
      );
    } else if (type === 'identifierSumGroup') {
      if (!rows.length) {
        return null;
      }
      const summedItems = rows.map((row) => `${getValue(row)}`).join(' + ');
      if (
        rows[0].datatype ===
        t(['oee:availability'], {
          defaultValue: 'Availability',
        })
      ) {
        return (
          <span className={classes.wordSpace}>
            {`${t(['oee:availability'], { defaultValue: 'Availability' })} `}
            {' = '}
            <span style={{ wordSpacing: 'normal' }}>
              {` ${t(['shared:totalTime'], {
                defaultValue: 'Total Time',
              })}`}
            </span>{' '}
            &#8722; {`(${summedItems})`}
          </span>
        );
      } else if (
        rows[0].datatype ===
        t(['oee:performance'], {
          defaultValue: 'Performance',
        })
      ) {
        return (
          <span className={classes.wordSpace}>
            {t(['oee:performance'], { defaultValue: 'Performance' })}
            {' = '}
            {t(['shared:availability'], { defaultValue: 'Availability' })} &#8722;
            {`(${summedItems})`}
          </span>
        );
      } else if (
        rows[0].datatype ===
        t(['oee:quality'], {
          defaultValue: 'Quality',
        })
      ) {
        return (
          <span className={classes.wordSpace}>
            {t(['oee:quality'], { defaultValue: 'Quality' })}
            {' = '}
            {t(['shared:performance'], { defaultValue: 'Performance' })} &#8722;
            {`(${summedItems})`}
          </span>
        );
      }
    } else if (type === 'durationSumGroup') {
      // return null;
      if (!rows.length) {
        return null;
      }
      let avail = allRows
        .filter(
          (r) =>
            r.datatype ===
            t(['oee:availability'], {
              defaultValue: 'Availability',
            }),
        )
        .reduce((acc, row) => acc + row.duration, 0);
      const perf = allRows
        .filter(
          (r) =>
            r.datatype ===
            t(['oee:performance'], {
              defaultValue: 'Performance',
            }),
        )
        .reduce((acc, row) => acc + row.duration, 0);
      const quality = allRows
        .filter(
          (r) =>
            r.datatype ===
            t(['oee:quality'], {
              defaultValue: 'Quality',
            }),
        )
        .reduce((acc, row) => acc + row.duration, 0);
      if (
        rows[0].datatype ===
        t(['oee:availability'], {
          defaultValue: 'Availability',
        })
      ) {
        return utils.formatDistanceStrict(0, (totalTime ?? 0) - avail, { unit: 'minute' });
      } else if (
        rows[0].datatype ===
        t(['oee:performance'], {
          defaultValue: 'Performance',
        })
      ) {
        avail = allRows
          .filter(
            (r) =>
              r.datatype ===
              t(['oee:availability'], {
                defaultValue: 'Availability',
              }),
          )
          .reduce((acc, row) => acc + row.duration, 0);
        return utils.formatDistanceStrict(0, (totalTime ?? 0) - avail - perf, { unit: 'minute' });
      } else if (
        rows[0].datatype ===
        t(['oee:quality'], {
          defaultValue: 'Quality',
        })
      ) {
        return utils.formatDistanceStrict(0, (totalTime ?? 0) - avail - perf - quality, { unit: 'minute' });
      }
    } else if (type === 'calculation') {
      if (!rows.length || !totalTime) {
        return '=> 0 %';
      }
      let time: number | undefined;
      if (
        rows[0].datatype ===
        t(['oee:availability'], {
          defaultValue: 'Availability',
        })
      ) {
        time = totalTime;
      } else if (
        rows[0].datatype ===
        t(['oee:performance'], {
          defaultValue: 'Performance',
        })
      ) {
        const avail = allRows
          .filter(
            (r) =>
              r.datatype ===
              t(['oee:availability'], {
                defaultValue: 'Availability',
              }),
          )
          .reduce((acc, row) => acc + row.duration, 0);
        time = totalTime - avail;
      } else if (
        rows[0].datatype ===
        t(['oee:quality'], {
          defaultValue: 'Quality',
        })
      ) {
        const avail = allRows
          .filter(
            (r) =>
              r.datatype ===
              t(['oee:availability'], {
                defaultValue: 'Availability',
              }),
          )
          .reduce((acc, row) => acc + row.duration, 0);
        const perf = allRows
          .filter(
            (r) =>
              r.datatype ===
              t(['oee:performance'], {
                defaultValue: 'Performance',
              }),
          )
          .reduce((acc, row) => acc + row.duration, 0);
        time = totalTime - avail - perf;
      }
      return (
        <span className={classes.wordSpace}>
          {`${rightArrow} ${(!time
            ? 0
            : ((time - rows.reduce((acc, row) => acc + row.duration, 0)) / time) * 100
          ).toFixed(2)}%`}
        </span>
      );
    } else if (type === 'calculationTotal' || type === 'durationTotal') {
      if (!rows.length || !totalTime) {
        return <span>{rightArrow}</span>;
      }
      const calcs = {
        Availability: 0,
        Performance: 0,
        Quality: 0,
      };
      rows.map((row) => {
        calcs[row.datatype as keyof typeof calcs] += row.duration;
      });
      calcs.Availability = totalTime - calcs.Availability;
      calcs.Performance = calcs.Availability - calcs.Performance;
      calcs.Quality = calcs.Performance - calcs.Quality;
      if (type === 'durationTotal') {
        return (
          <span className={classes.wordSpace}>
            {`${((calcs.Availability / totalTime) * 100).toFixed(2)}%`} &#xb7;{' '}
            {`${(calcs.Availability === 0 ? 0 : (calcs.Performance / calcs.Availability) * 100).toFixed(2)}%`} &#xb7;{' '}
            {`${(calcs.Performance === 0 ? 0 : (calcs.Quality / calcs.Performance) * 100).toFixed(2)}%`}
          </span>
        );
      }
      return (
        <span className={classes.wordSpace}>
          {`= ${(calcs.Availability === 0 || calcs.Performance === 0
            ? 0
            : (calcs.Availability / totalTime) *
              (calcs.Performance / calcs.Availability) *
              (calcs.Quality / calcs.Performance) *
              100
          ).toFixed(2)}%`}
        </span>
      );
    }
    return IntegratedSummary.defaultCalculator(type, rows, getValue);
  };
const reshapeData = ({ t, data }: { t: i18next.TFunction; data?: Data | null }) => {
  const reshapeStop = (stop: Schema.StopCauseStat) => {
    return {
      type: stop.type || Schema.StopType.DOWNTIME,
      name: stop.type
        ? stop.stopCauseName
        : stop.isMicroStop
        ? t(['shared:microStop'], { defaultValue: 'Micro stops' })
        : t(['shared:unregisteredStops'], { defaultValue: 'Unregistered stops' }),
      description: stop.type
        ? stop.stopCauseDescription
        : stop.isMicroStop
        ? t(['oee:timeLostDueToMicroStops'], { defaultValue: 'Time lost due to micro stops' })
        : t(['oee:timeLostDueToUnregisteredStops'], { defaultValue: 'Time lost due to unregistered stops' }),
      duration: stop.accDuration,
      OEEPercentage: '',
    };
  };
  const { scrapLoss, speedLoss } = data?.oee ?? { scrapLoss: 0, speedLoss: 0 };
  const stopStats = [...(data?.stopStats || [])].sort((a, b) => b.accDuration - a.accDuration);
  const avaliability = stopStats.filter(
    (s) =>
      s.type === Schema.StopType.NO_ACT ||
      s.type === Schema.StopType.NON_PROD ||
      s.type === Schema.StopType.BATCH_NON_PROD,
  );
  const downtimeStops = stopStats.filter((s) => s.type === Schema.StopType.DOWNTIME);
  const unregisteredStops = stopStats.filter((s) => !s.type && !s.isMicroStop);
  const microStops = stopStats.filter((s) => !s.type && s.isMicroStop);
  const returnData = [];
  let cnt = 1;
  if (avaliability?.length) {
    avaliability.forEach((s) =>
      returnData.push({
        ...reshapeStop(s),
        identifier: `A${cnt++}`,
        datatype: t(['oee:availability'], {
          defaultValue: 'Availability',
        }),
      }),
    );
  } else {
    returnData.push({
      identifier: `A${cnt++}`,
      datatype: t(['oee:availability'], {
        defaultValue: 'Availability',
      }),
      type: 'NON_PROD',
      name: t(['oee:availabilityLoss'], { defaultValue: 'Availability loss' }),
      description: t(['oee:timeLostDueToAvailablity'], { defaultValue: 'Time lost due to availablity' }),
      duration: 0,
      OEEPercentage: '',
    });
  }
  cnt = 1;
  returnData.push({
    identifier: `P${cnt++}`,
    datatype: t(['oee:performance'], {
      defaultValue: 'Performance',
    }),
    type: 'DOWNTIME',
    name: t(['oee:speedLoss'], { defaultValue: 'Speed loss' }),
    description: t(['oee:timeLostDueToSpeed'], {
      defaultValue: 'Time lost due to running at lower than validated speed',
    }),
    duration: speedLoss,
    OEEPercentage: '',
  });
  if (unregisteredStops && unregisteredStops.length) {
    returnData.push({
      ...reshapeStop(unregisteredStops[0]),
      identifier: `P${cnt++}`,
      datatype: t(['oee:performance'], {
        defaultValue: 'Performance',
      }),
    });
  }
  if (microStops && microStops.length) {
    returnData.push({
      ...reshapeStop(microStops[0]),
      identifier: `P${cnt++}`,
      datatype: t(['oee:performance'], {
        defaultValue: 'Performance',
      }),
    });
  }
  downtimeStops?.forEach((s) =>
    returnData.push({
      ...reshapeStop(s),
      identifier: `P${cnt++}`,
      datatype: t(['oee:performance'], {
        defaultValue: 'Performance',
      }),
    }),
  );
  cnt = 1;
  returnData.push({
    identifier: `Q${cnt++}`,
    datatype: t(['oee:quality'], {
      defaultValue: 'Quality',
    }),
    type: 'DOWNTIME',
    name: t(['shared:scrap'], { defaultValue: 'Scrap' }),
    description: t(['oee:timeLostDueToScrap'], { defaultValue: 'Time lost due to scrapped items' }),
    duration: scrapLoss,
    OEEPercentage: '',
  });
  return returnData;
};
const messages: TableSummaryRowBase.LocalizationMessages = {
  sum: 'OEE<sub>1</sub>',
};
interface Properties {
  data?: Data | null;
  error?: ApolloError;
  loading?: boolean;
  refetch: (start: Date, end: Date | null) => void;
}
interface Row {
  datatype: string;
  identifier: string;
  type: string;
  name: string;
  description: string;
  duration: number;
  OEEPercentage: string;
}
interface State {
  columns: Array<{
    name: string;
    title: string;
  }>;
  rows: Row[];
  grouping: Array<{
    columnName: string;
  }>;
  tableColumnExtensions: Array<{
    columnName: string;
    align?: 'right' | 'center' | 'left';
  }>;
  totalSummaryItems: Array<{
    columnName: string;
    type: string;
  }>;
  groupSummaryItems: Array<{
    columnName: string;
    type: string;
  }>;
  stopCauseTypeColumns: string[];
  durationTypeColumns: string[];
  // $;
  nameColumns: string[];
}
class OEETableCard extends React.Component<
  Properties & WithTranslation & WithUtils & WithStyles<typeof styles>,
  State
> {
  static getDerivedStateFromProps(
    props: Readonly<Properties & WithTranslation & WithUtils & WithStyles<typeof styles>>,
    state: State,
  ) {
    return { ...state, rows: reshapeData(props) };
  }
  readonly state: State = {
    columns: [
      { name: 'datatype', title: this.props.t(['oee:oeeGroup'], { defaultValue: 'OEE Group' }) },
      { name: 'identifier', title: this.props.t(['oee:variable'], { defaultValue: 'Variable' }) },
      { name: 'name', title: this.props.t(['shared:name'], { defaultValue: 'Name' }) },
      { name: 'duration', title: this.props.t(['shared:duration'], { defaultValue: 'Duration' }) },
      { name: 'OEEPercentage', title: this.props.t(['oee:oeePercentage'], { defaultValue: 'OEE Percentage' }) },
    ],
    rows: reshapeData(this.props),
    grouping: [{ columnName: 'datatype' }],
    tableColumnExtensions: [{ columnName: 'duration', align: 'right' }],
    totalSummaryItems: [
      { columnName: 'identifier', type: 'identifierSumTotal' },
      { columnName: 'duration', type: 'durationTotal' },
      { columnName: 'OEEPercentage', type: 'calculationTotal' },
    ],
    groupSummaryItems: [
      { columnName: 'identifier', type: 'identifierSumGroup' },
      { columnName: 'duration', type: 'durationSumGroup' },
      { columnName: 'OEEPercentage', type: 'calculation' },
    ],
    stopCauseTypeColumns: ['identifier'],
    durationTypeColumns: ['duration'],
    nameColumns: ['name'],
  };
  public render() {
    const { classes, theme, utils, data, t } = this.props;
    const {
      rows,
      columns,
      totalSummaryItems,
      tableColumnExtensions,
      grouping,
      groupSummaryItems,
      stopCauseTypeColumns,
      durationTypeColumns,
      nameColumns,
    } = this.state;
    const totalTime = data?.oee?.totalEquipmentTime;

    return (
      <MUIGrid item xs={12} sm={12} md={12}>
        <Card elevation={1} className={classes.card}>
          <CardContent>
            <Grid rows={rows} columns={columns}>
              <StopsCauseTypeProvider for={stopCauseTypeColumns} theme={theme} t={t} />
              <NameTooltipProvider for={nameColumns} theme={theme} />
              <DurationTypeProvider for={durationTypeColumns} utils={utils} t={t} warnWhenNegative />
              <GroupingState
                grouping={grouping}
                defaultExpandedGroups={[
                  t(['oee:availability'], {
                    defaultValue: 'Availability',
                  }),
                  t(['oee:performance'], {
                    defaultValue: 'Performance',
                  }),
                  t(['oee:quality'], {
                    defaultValue: 'Quality',
                  }),
                ]}
              />
              <SummaryState totalItems={totalSummaryItems} groupItems={groupSummaryItems} />
              <IntegratedGrouping />
              <IntegratedSummary calculator={OEESummaryCalculator(t, totalTime, rows, classes, utils)} />
              <Table columnExtensions={tableColumnExtensions} />
              <TableHeaderRow />
              <TableGroupRow cellComponent={GroupCellWithoutGroupname} />
              <TableSummaryRow
                groupRowComponent={GroupRowWithColSpan(classes.customRow)}
                totalRowComponent={GroupRowWithColSpan(classes.customTotalRow)}
                messages={messages}
              />
              <Template name="tableCell" predicate={this.onPredicate}>
                {(
                  params: TableBase.CellProps & {
                    tableColumn: TableColumn & {
                      style?: React.CSSProperties;
                    };
                  },
                ) => (
                  <TemplateConnector>
                    {(getters) => {
                      const { tableRow, tableColumn, ...props } = params;
                      let columnSummaries = [];
                      if (getters?.groupSummaryValues) {
                        if (isGroupSummaryTableCell(tableRow, tableColumn)) {
                          columnSummaries = getColumnSummaries(
                            getters.groupSummaryItems.map((item: any) => ({ ...item, type: Symbol(item.type) })),
                            tableColumn.column?.name || '',
                            getters.groupSummaryValues[String(tableRow.row.compoundKey)],
                          );
                        } else {
                          columnSummaries = getColumnSummaries(
                            getters.totalSummaryItems.map((item: any) => ({ ...item, type: Symbol(item.type) })),
                            tableColumn.column?.name || '',
                            getters.totalSummaryValues,
                          );
                        }
                      }

                      return (
                        <TableCell
                          {...props}
                          style={{
                            ...tableColumn.style,
                            textAlign: tableColumn.align,
                            paddingLeft: '8px',
                            paddingRight: '8px',
                            color: theme.palette.text.primary,
                            fontWeight: 400,
                            borderBottom: '1px solid rgba(224, 224, 224, 1)',
                            textOverflow: 'wrap',
                          }}
                        >
                          {columnSummaries.map((summary: { type: string; value: any }) => {
                            return <React.Fragment key={String(summary.value)}>{summary.value}</React.Fragment>;
                          })}
                        </TableCell>
                      );
                    }}
                  </TemplateConnector>
                )}
              </Template>
            </Grid>
          </CardContent>
        </Card>
      </MUIGrid>
    );
  }
  private onPredicate = (params: object) => {
    const { tableRow, tableColumn } = params as TableBase.CellProps;
    return isGroupSummaryTableCell(tableRow, tableColumn) || isTotalSummaryTableCell(tableRow, tableColumn);
  };
}
const enhance = compose<unknown, Properties>(
  withUtils,
  withTranslation(['shared', 'device', 'oee']),
  withStyles(styles, { withTheme: true }),
);
export default enhance(OEETableCard as React.ComponentType<unknown>);
